import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import UniversityFilter from './UniversityFilter';
import AmbassadorStatsCards from './AmbassadorStatsCards';
import AmbassadorTable from './AmbassadorTable';
import RecordPaymentModal from './RecordPaymentModal';

const AmbassadorOverview = () => {
  const navigate = useNavigate();
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [stats, setStats] = useState(null);
  const [ambassadors, setAmbassadors] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedAmbassador, setSelectedAmbassador] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.junctionverse.com';

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = selectedUniversity ? { universityId: selectedUniversity.id } : {};
      
      const response = await axios.get(`${API_BASE_URL}/admin/campus/ambassador-overview/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params
      });

      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching ambassador stats:', err);
      setError('Failed to load statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedUniversity, API_BASE_URL]);

  const fetchAmbassadorList = useCallback(async () => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = {
        page: currentPage,
        limit: 50
      };
      
      if (selectedUniversity) {
        params.universityId = selectedUniversity.id;
      }
      
      const response = await axios.get(`${API_BASE_URL}/admin/campus/ambassador-overview/list`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params
      });

      setAmbassadors(response.data.ambassadors || []);
      setPagination(response.data.pagination || null);
      setError(null);
    } catch (err) {
      console.error('Error fetching ambassador list:', err);
      setError('Failed to load ambassador list. Please try again.');
    } finally {
      setTableLoading(false);
    }
  }, [selectedUniversity, currentPage, API_BASE_URL]);

  // Fetch stats when university filter changes
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Fetch ambassador list when university filter or page changes
  useEffect(() => {
    fetchAmbassadorList();
  }, [fetchAmbassadorList]);


  const handleUniversityChange = (university) => {
    setSelectedUniversity(university);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRecordPayment = (ambassador) => {
    console.log('[AmbassadorOverview] handleRecordPayment called', {
      ambassador: ambassador ? { id: ambassador.id, name: ambassador.name } : null,
      timestamp: new Date().toISOString()
    });
    setSelectedAmbassador(ambassador);
    setShowPaymentModal(true);
    console.log('[AmbassadorOverview] Modal state set to show');
  };

  const handlePaymentRecorded = (payment) => {
    // Refresh ambassador list to show updated payment amounts
    fetchAmbassadorList();
    // Optionally refresh stats as well
    fetchStats();
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedAmbassador(null);
  };

  const handleAmbassadorClick = (ambassador) => {
    navigate(`/campus/ambassador-overview/${ambassador.id}`);
  };

  return (
    <div>
      <div className='d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-4 gap-3'>
        <div>
          <h1 className="mb-2 fw-bold" style={{ fontSize: '1.875rem', color: '#1e293b' }}>Ambassador Overview</h1>
          <p className="text-muted mb-0 small">Analyze ambassador performance and recruitment metrics</p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* University Filter */}
      <UniversityFilter
        onUniversityChange={handleUniversityChange}
        selectedUniversityId={selectedUniversity?.id}
      />

      {/* Stats Cards */}
      <AmbassadorStatsCards stats={stats} loading={loading} />

      {/* Last 4 Weeks Chart */}
      {!loading && stats && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border rounded-xl">
              <div className="p-4">
                <h5 className="fw-bold mb-3">Last 4 Weeks</h5>
                {stats.last4Weeks && stats.last4Weeks.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.last4Weeks}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="week"
                        tick={{ fontSize: 10 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke="#4C1D95"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        name="Users Onboarded"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted text-center py-4">No data available for the last 4 weeks</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ambassador Table */}
      <div className="mt-4">
        <div className="card shadow-sm border rounded-3">
          <div className="card-header bg-white border-bottom px-4 py-3">
            <h4 className="mb-0 fw-semibold">Ambassador Performance</h4>
          </div>
          <div className="card-body p-0">
            <AmbassadorTable
          ambassadors={ambassadors}
          pagination={pagination}
          loading={tableLoading}
          onPageChange={handlePageChange}
          onRecordPayment={handleRecordPayment}
          onAmbassadorClick={handleAmbassadorClick}
        />
          </div>
        </div>
      </div>

      {/* Record Payment Modal */}
      <RecordPaymentModal
        show={showPaymentModal}
        onClose={handleClosePaymentModal}
        ambassador={selectedAmbassador}
        onPaymentRecorded={handlePaymentRecorded}
      />
    </div>
  );
};

export default AmbassadorOverview;

